import gsap from "gsap";
import { Manager, Swipe, Tap } from "hammerjs";
import { Color, Group, Mesh, MeshLambertMaterial, PerspectiveCamera, Raycaster, Vector2 } from 'three';
import { triggerDrawControl } from "../../..";
import Renderer from "../../Renderer";
import { LoadedGLTFs, LoadedTextures } from "../../utils/types";
import { hidePenSelectionHand } from "../../../dom";
import { Params } from "../../../utils/types";


let experience = <HTMLElement>document.querySelector('.experience');

export class PenSelection {
    public instance: Group;
    public carousel: Group;
    public pens: Array<Mesh | Group>;
    public mouse: Vector2;
    public renderer: Renderer;
    public camera: PerspectiveCamera;
    public raycaster: Raycaster;
    public hammer: HammerManager;
    public currentPen: number;
    public toggling: boolean;

    constructor(renderer: Renderer, camera: PerspectiveCamera, gltfs: LoadedGLTFs, textures: LoadedTextures, urlParams: Params) {
        this.instance = new Group();
        this.carousel = new Group();
        this.instance.position.set(0, 0, -1);
        this.pens = [];
        this.instance.visible = false;
        this.mouse = new Vector2();
        this.renderer = renderer;
        this.camera = camera;
        this.raycaster = new Raycaster();
        this.hammer = new Manager(experience, { recognizers: [[Tap], [Swipe]], });
        this.currentPen = urlParams.penType == 'brush' ? 0 : 1;
        this.toggling = false;
        this.setUpPens(gltfs)
    }


    public setUpPens(gltfs: LoadedGLTFs) {
        this.carousel.rotation.y = Math.PI / 3;
        this.carousel.position.set(0.1, 0.2, 0);
        this.instance.add(this.carousel);

        for (let i = 0; i < 2; i++) {
            let pen = i == 0 ? gltfs.pen_brush.scene.clone() : gltfs.pen_bullet.scene.clone()

            pen.scale.set(5, 5, 5);
            pen.position.set(0, -0.2, 0);
            pen.rotation.set(Math.PI, 0, 0)
            pen.traverse((node: object) => {
                if (node instanceof Mesh) {
                    if (node.name == "transparent_part") {
                        node.material.color = new Color(1, 1, 1);
                        node.material.metalness = 1;
                    }
                    node.material.emissive = new Color(0, 0, 0);
                    node.material.transparent = true;
                    if (node.name != "transparent_part")
                        node.material.opacity = 0;

                    if (node.name == "cap_main" || node.name == "coloured_part") {
                        node.visible = false;
                    }
                }
            });
            let group = new Group();
            group.position.x = this.currentPen == i ? -0.2 : 0.2
            group.rotation.y = -Math.PI / 3;
            group.add(pen);
            this.carousel.add(group);
            this.pens.push(group)
        }
    }

    public revealPenSelection = () => {
        this.instance.visible = true;
        this.instance.traverse((node: object) => {
            if (node instanceof Mesh) {
                gsap.to(node.material, {
                    opacity: 1,
                    duration: 0.5,
                    ease: "sine.easeOut",
                    onUpdate: () => {
                        if (node.name == "transparent_part") {
                            node.material.opacity = node.material.opacity / 2;
                        }
                    },
                });
            }
        });
        this.hammer.on("tap", e => this.tap(e));
        this.hammer.on("swipeleft", e => this.swipe(e));
        this.hammer.on("swiperight", e => this.swipe(e));
    }

    public hidePenSelection = async () => {
        this.hammer.off("tap");
        this.hammer.off("swipeleft");
        this.hammer.off("swipeight");
        this.instance.visible = false
        return new Promise<void>((resolve, _) => {
            this.instance.traverse((node: object) => {
                if (node instanceof Mesh) {
                    gsap.to(node.material, {
                        opacity: 0,
                        duration: 0.5,
                        ease: "sine.easeOut",
                        onUpdate: () => {
                            if (node.name == "transparent_part") {
                                node.material.opacity = node.material.opacity / 2;
                            }
                        },
                        onComplete: () => {
                            resolve()
                        },
                    });
                }
            });
        });
    }

    public setPenColourHex = (colour: string) => {
        this.pens.forEach((pen, i) => {
            pen.traverse(function (node: object) {
                if (node instanceof Mesh) {
                    if (node.name == "nib" || node.name == "body_2" || node.name == "bottom" || node.name == "coloured_part") {
                        node.material = new MeshLambertMaterial({ color: new Color(colour).convertSRGBToLinear() });
                    }
                }
            });
        });

    }

    public togglePen = (direction: number) => {
        if (!this.toggling) {
            this.toggling = true;
            this.currentPen = +!this.currentPen;
            triggerDrawControl(this.currentPen == 0 ? 'brush' : 'bullet')

            const count = { val: 0 };
            let carouselRot = this.carousel.rotation.y;
            gsap.to(count, {
                val: 1,
                duration: 1,
                ease: "sine.easeOut",
                onUpdate: () => {
                    this.carousel.rotation.y = carouselRot + (count.val * Math.PI * direction)
                    this.pens.forEach((pen, i) => {
                        pen.rotation.y = -this.carousel.rotation.y
                    });
                },
                onComplete: () => {
                    this.toggling = false
                }
            });
        }
    }

    public tap(event: HammerInput) {
        const normalisedCoords = {
            x: (event.center.x / window.innerWidth) * 2 - 1,
            y: (event.center.y / window.innerHeight) * -2 + 1,
        };
        this.raycaster.setFromCamera(normalisedCoords, this.camera);
        this.pens.forEach((pen, i) => {
            if (this.raycaster.intersectObject(pen, true).length > 0) {
                this.togglePen(1)
            }
        });
    }

    public swipe(event: HammerInput) {
        hidePenSelectionHand()
        if (event.type == "swiperight")
            this.togglePen(1)

        if (event.type == "swipeleft")
            this.togglePen(-1)
    }
}